/**
 * GLSL port of src/core/Rand.js noise.
 * owner: materials
 *
 * `perlin3`, `fbm`, `ridged`, `worleyT` are BIT-EXACT ports of the CPU functions in
 * Rand.js: they read the same 256-entry permutation table (uploaded as an R8 texture)
 * and use the same gradient set, fade curve and octave weighting. That means a value
 * sampled on the GPU during texture synthesis matches the value the CPU would compute
 * for the same coordinates — level/FX code can therefore query Rand.js and get answers
 * that agree with what is baked into the maps.
 *
 * The `*HQ` variants add a second permutation tap for finer cell jitter. They are used
 * for purely visual detail (aggregate speckle, pebbles) where the 1/15 jitter quantisation
 * of the CPU worley would read as a visible grid.
 *
 * Everything with a `t` prefix (tfbm2, tridge, tvor…) is TILEABLE: the 2D domain is
 * wrapped onto a torus (the trick used by Rand.tileableFbm) so uv and uv+1 sample the
 * exact same value at any frequency, integer or not.
 */

export const NOISE_GLSL = /* glsl */`
#define TAU 6.283185307179586

uniform sampler2D uPerm;   // 256 x 1, R8 — Rand.js permutation table

int pperm(int i){ return int(texelFetch(uPerm, ivec2(i & 255, 0), 0).r * 255.0 + 0.5); }

float sat(float x){ return clamp(x, 0.0, 1.0); }
vec2  sat2(vec2 x){ return clamp(x, 0.0, 1.0); }
vec3  sat3(vec3 x){ return clamp(x, 0.0, 1.0); }
float ss(float e0, float e1, float x){ float t = sat((x - e0) / (e1 - e0)); return t * t * (3.0 - 2.0 * t); }

/* cheap hashes (not CPU-matched — used only for per-cell decoration) */
float h11(float p){ p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
float h21(vec2 p){ vec3 p3 = fract(p.xyx * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
vec2  h22(vec2 p){ vec3 p3 = fract(p.xyx * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.xx + p3.yz) * p3.zy); }
vec3  h23(vec2 p){ vec3 p3 = fract(p.xyx * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yxz + 33.33); return fract((p3.xxy + p3.yzz) * p3.zyx); }

/* ---- Perlin (exact port of Rand.perlin3) ------------------------------- */

float grad3(int h, vec3 p){
  int j = h & 15;
  float u = (j < 8) ? p.x : p.y;
  float v = (j < 4) ? p.y : ((j == 12 || j == 14) ? p.x : p.z);
  return ((j & 1) == 0 ? u : -u) + ((j & 2) == 0 ? v : -v);
}

float perlin3(vec3 p){
  vec3 fl = floor(p);
  int X = int(fl.x) & 255;
  int Y = int(fl.y) & 255;
  int Z = int(fl.z) & 255;
  vec3 f = p - fl;
  vec3 w = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);

  int A  = pperm(X) + Y;
  int AA = pperm(A) + Z;
  int AB = pperm(A + 1) + Z;
  int B  = pperm(X + 1) + Y;
  int BA = pperm(B) + Z;
  int BB = pperm(B + 1) + Z;

  float x00 = mix(grad3(pperm(AA),     f + vec3( 0.0,  0.0,  0.0)), grad3(pperm(BA),     f + vec3(-1.0,  0.0,  0.0)), w.x);
  float x10 = mix(grad3(pperm(AB),     f + vec3( 0.0, -1.0,  0.0)), grad3(pperm(BB),     f + vec3(-1.0, -1.0,  0.0)), w.x);
  float x01 = mix(grad3(pperm(AA + 1), f + vec3( 0.0,  0.0, -1.0)), grad3(pperm(BA + 1), f + vec3(-1.0,  0.0, -1.0)), w.x);
  float x11 = mix(grad3(pperm(AB + 1), f + vec3( 0.0, -1.0, -1.0)), grad3(pperm(BB + 1), f + vec3(-1.0, -1.0, -1.0)), w.x);

  return mix(mix(x00, x10, w.y), mix(x01, x11, w.y), w.z);
}

float perlin2(vec2 p){ return perlin3(vec3(p, 0.31830988)); }

/* ---- Worley (exact port of Rand.worley2) ------------------------------- */

vec3 worleyT(vec2 p, int period){
  vec2 ip = floor(p);
  float f1 = 1e9, f2 = 1e9;
  int id = 0;
  for (int oy = -1; oy <= 1; oy++){
    for (int ox = -1; ox <= 1; ox++){
      int cx = int(ip.x) + ox;
      int cy = int(ip.y) + oy;
      int hx = cx, hy = cy;
      if (period > 0){ hx = ((cx % period) + period) % period; hy = ((cy % period) + period) % period; }
      int hh = pperm(pperm(hx & 255) + (hy & 255));
      vec2 c = vec2(float(cx) + float(hh & 15) / 15.0, float(cy) + float((hh >> 4) & 15) / 15.0);
      float d = length(c - p);
      if (d < f1){ f2 = f1; f1 = d; id = hh; }
      else if (d < f2){ f2 = d; }
    }
  }
  return vec3(f1, f2, float(id) / 255.0);
}

/* higher-jitter worley — visual only */
vec3 worleyHQ(vec2 p, int period){
  vec2 ip = floor(p);
  float f1 = 1e9, f2 = 1e9;
  float id = 0.0;
  for (int oy = -1; oy <= 1; oy++){
    for (int ox = -1; ox <= 1; ox++){
      int cx = int(ip.x) + ox;
      int cy = int(ip.y) + oy;
      int hx = cx, hy = cy;
      if (period > 0){ hx = ((cx % period) + period) % period; hy = ((cy % period) + period) % period; }
      int h0 = pperm(pperm(hx & 255) + (hy & 255));
      int h1 = pperm(h0 + 37);
      int h2 = pperm(h1 + 101);
      vec2 c = vec2(
        float(cx) + (float(h0 & 15) * 16.0 + float(h1 & 15)) / 255.0,
        float(cy) + (float((h0 >> 4) & 15) * 16.0 + float((h1 >> 4) & 15)) / 255.0);
      float d = length(c - p);
      if (d < f1){ f2 = f1; f1 = d; id = float(h2) / 255.0; }
      else if (d < f2){ f2 = d; }
    }
  }
  return vec3(f1, f2, id);
}

/* ---- tileable fractal noise (torus projection, cf. Rand.tileableFbm) --- */

vec2 _torA(float a, float b, vec2 f){ return vec2(cos(a), sin(a)) * f.x / TAU; }
vec2 _torB(float a, float b, vec2 f){ return vec2(cos(b), sin(b)) * f.y / TAU; }

float tperlin(vec2 uv, vec2 freq, float seed){
  float a = uv.x * TAU, b = uv.y * TAU;
  vec2 A = _torA(a, b, freq), B = _torB(a, b, freq);
  return perlin3(vec3(A.x, A.y, B.x) + seed) * 0.5 + perlin3(vec3(A.y, B.x, B.y) + seed * 1.7 + 5.0) * 0.5;
}

float tfbm2(vec2 uv, vec2 freq, int oct, float gain, float seed){
  float amp = 1.0, sum = 0.0, norm = 0.0;
  vec2 f = freq;
  float a = uv.x * TAU, b = uv.y * TAU;
  for (int i = 0; i < oct; i++){
    vec2 A = _torA(a, b, f), B = _torB(a, b, f);
    sum += amp * (perlin3(vec3(A.x, A.y, B.x) + seed) * 0.5 + perlin3(vec3(A.y, B.x, B.y) + seed * 1.7 + 5.0) * 0.5);
    norm += amp;
    amp *= gain;
    f *= 2.0;
  }
  return sum / norm;
}

float tridge(vec2 uv, vec2 freq, int oct, float gain, float seed){
  float amp = 1.0, sum = 0.0, norm = 0.0;
  vec2 f = freq;
  float a = uv.x * TAU, b = uv.y * TAU;
  for (int i = 0; i < oct; i++){
    vec2 A = _torA(a, b, f), B = _torB(a, b, f);
    float n = perlin3(vec3(A.x, A.y, B.x) + seed) * 0.5 + perlin3(vec3(A.y, B.x, B.y) + seed * 1.7 + 5.0) * 0.5;
    n = 1.0 - abs(n) * 2.0;
    sum += amp * n * n;
    norm += amp;
    amp *= gain;
    f *= 2.0;
  }
  return sum / norm;
}

/* billowy / turbulent */
float tturb(vec2 uv, vec2 freq, int oct, float gain, float seed){
  float amp = 1.0, sum = 0.0, norm = 0.0;
  vec2 f = freq;
  float a = uv.x * TAU, b = uv.y * TAU;
  for (int i = 0; i < oct; i++){
    vec2 A = _torA(a, b, f), B = _torB(a, b, f);
    float n = perlin3(vec3(A.x, A.y, B.x) + seed) * 0.5 + perlin3(vec3(A.y, B.x, B.y) + seed * 1.7 + 5.0) * 0.5;
    sum += amp * abs(n) * 2.0;
    norm += amp;
    amp *= gain;
    f *= 2.0;
  }
  return sum / norm;
}

/* tileable worley. freq = cells per tile (rounded to an integer so the period wraps). */
vec3 tvor(vec2 uv, float freq, float seed){
  int per = max(1, int(freq + 0.5));
  return worleyHQ(uv * float(per) + vec2(seed, seed * 1.7), per);
}
vec3 tvorCPU(vec2 uv, float freq, float seed){
  int per = max(1, int(freq + 0.5));
  return worleyT(uv * float(per) + vec2(seed, seed * 1.7), per);
}
/* anisotropic tileable worley (stretched cells: fibres, brushed metal, wood) */
vec3 tvorA(vec2 uv, vec2 freq, float seed){
  ivec2 per = max(ivec2(1), ivec2(freq + 0.5));
  vec2 p = uv * vec2(per) + vec2(seed, seed * 1.7);
  vec2 ip = floor(p);
  float f1 = 1e9, f2 = 1e9, id = 0.0;
  for (int oy = -1; oy <= 1; oy++){
    for (int ox = -1; ox <= 1; ox++){
      int cx = int(ip.x) + ox, cy = int(ip.y) + oy;
      int hx = ((cx % per.x) + per.x) % per.x;
      int hy = ((cy % per.y) + per.y) % per.y;
      int h0 = pperm(pperm(hx & 255) + (hy & 255));
      int h1 = pperm(h0 + 37);
      vec2 c = vec2(
        float(cx) + (float(h0 & 15) * 16.0 + float(h1 & 15)) / 255.0,
        float(cy) + (float((h0 >> 4) & 15) * 16.0 + float((h1 >> 4) & 15)) / 255.0);
      float d = length(c - p);
      if (d < f1){ f2 = f1; f1 = d; id = float(h1) / 255.0; }
      else if (d < f2){ f2 = d; }
    }
  }
  return vec3(f1, f2, id);
}

/* ---- colour helpers ---------------------------------------------------- */

vec3 linearToSRGB(vec3 c){
  return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(0.41666)) - 0.055, step(0.0031308, c));
}
vec3 srgbToLinear(vec3 c){
  return mix(c * 0.0773993808, pow(c * 0.9478672986 + 0.0521327014, vec3(2.4)), step(0.04045, c));
}
/* perceptual-ish tint: keeps energy while shifting hue */
vec3 tint(vec3 base, vec3 t, float k){ return mix(base, base * t * 2.0, k); }
`;
